import {
  // eslint-disable-next-line no-restricted-imports
  Button as MuiButton,
  type ButtonProps as MuiButtonProps,
} from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router';
import {
  getHrefString,
  getLinkSecurityAttributes,
  isMailToLink,
  isRelativeLink,
  validateHref,
} from '~/components/Link/linkUtils';
import type { RouteLocationOptions } from '~/hooks/useRouter';

type LinkButtonProps = {
  href: string | RouteLocationOptions;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  replace?: boolean;
  preventScrollReset?: boolean;
};

type ButtonOnlyProps = {
  href?: never;
  target?: never;
  rel?: never;
  replace?: never;
  preventScrollReset?: never;
};

export type ButtonType = Omit<MuiButtonProps, keyof LinkButtonProps>;

export type CustomButtonProps =
  | (Omit<MuiButtonProps, 'href'> & LinkButtonProps)
  | (MuiButtonProps & ButtonOnlyProps);

export const Button: React.FC<CustomButtonProps> = ({
  onClick,
  replace,
  preventScrollReset,
  ...props
}) => {
  const { href, rel, target, sx, ...rest } = props;

  const hrefString = (href ? getHrefString(href) : '').replace(
    new RegExp(`^${import.meta.env.VITE_ORIGIN}`),
    ''
  );
  validateHref(hrefString);

  const isMailTo = isMailToLink(hrefString);
  const isRelative = isRelativeLink(hrefString);

  const isExternalHref = hrefString && !isRelative && !isMailTo;
  const finalTargetAttribute =
    target || (isExternalHref ? '_blank' : undefined);
  if (href) {
    if (isExternalHref || isMailTo) {
      return (
        <MuiButton
          component="a"
          href={hrefString}
          onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
            if (onClick) {
              onClick(event as unknown as React.MouseEvent<HTMLButtonElement>);
            }
          }}
          referrerPolicy="unsafe-url"
          rel={getLinkSecurityAttributes(hrefString, rel)}
          target={finalTargetAttribute}
          sx={{ ...sx }}
          {...rest}
        />
      );
    }

    return (
      <MuiButton
        component={RouterLink}
        to={hrefString}
        replace={replace}
        preventScrollReset={preventScrollReset}
        onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
          if (onClick) {
            onClick(event as unknown as React.MouseEvent<HTMLButtonElement>);
          }
        }}
        rel={getLinkSecurityAttributes(hrefString, rel)}
        target={finalTargetAttribute}
        sx={{ ...sx }}
        {...rest}
      />
    );
  }

  return <MuiButton onClick={onClick} sx={{ ...sx }} {...rest} />;
};
Button.displayName = 'CustomButton';
